'use client';
import { useState } from 'react';

const CX = 460, CY = 365;
const CAT_R = 152;

function rad(d: number) { return (d * Math.PI) / 180; }
function pt(r: number, d: number) {
  const round = (n: number) => Math.round(n * 1000) / 1000;
  return { x: round(CX + r * Math.cos(rad(d))), y: round(CY + r * Math.sin(rad(d))) };
}
function anchor(x: number): 'start' | 'end' | 'middle' {
  return x > CX + 12 ? 'start' : x < CX - 12 ? 'end' : 'middle';
}

const CATS = [
  { id: 'frontend', label: 'Frontend',     angle: -135, color: '#c9c4ba' },
  { id: 'backend',  label: 'Backend',      angle: -45,  color: '#7ec8e3' },
  { id: 'ai',       label: 'AI & Data',    angle: 45,   color: '#b794f4' },
  { id: 'cloud',    label: 'Cloud & AWS',  angle: 135,  color: '#FF6B1A' },
] as const;

type CatId = 'frontend' | 'backend' | 'ai' | 'cloud';

// r = distance from center, s = outer circle radius (node size)
// Each category owns one 90° quadrant; r alternates near/far to stagger labels.
const TECHS: { id: string; label: string; cat: CatId; angle: number; r: number; s: number }[] = [
  // Frontend — top-left quadrant (-180° → -90°)
  { id: 'figma',     label: 'Figma',         cat: 'frontend', angle: -175, r: 268, s: 8  },
  { id: 'nuxt',      label: 'Nuxt',          cat: 'frontend', angle: -162, r: 297, s: 9  },
  { id: 'next',      label: 'Next.js',       cat: 'frontend', angle: -148, r: 262, s: 12 },
  { id: 'react',     label: 'React',         cat: 'frontend', angle: -135, r: 298, s: 13 },
  { id: 'tailwind',  label: 'Tailwind',      cat: 'frontend', angle: -122, r: 260, s: 10 },
  { id: 'ts',        label: 'TypeScript',    cat: 'frontend', angle: -108, r: 297, s: 11 },
  { id: 'js',        label: 'JavaScript',    cat: 'frontend', angle: -95,  r: 262, s: 10 },

  // Backend — top-right quadrant (-90° → 0°)
  { id: 'node',      label: 'Node.js',       cat: 'backend',  angle: -85, r: 297, s: 11 },
  { id: 'python',    label: 'Python',        cat: 'backend',  angle: -73, r: 260, s: 12 },
  { id: 'fastapi',   label: 'FastAPI',       cat: 'backend',  angle: -62, r: 297, s: 10 },
  { id: 'flask',     label: 'Flask',         cat: 'backend',  angle: -51, r: 258, s: 8  },
  { id: 'django',    label: 'Django',        cat: 'backend',  angle: -39, r: 294, s: 10 },
  { id: 'rest',      label: 'REST APIs',     cat: 'backend',  angle: -28, r: 256, s: 9  },
  { id: 'ws',        label: 'WebSockets',    cat: 'backend',  angle: -16, r: 296, s: 8  },
  { id: 'graphql',   label: 'GraphQL',       cat: 'backend',  angle: -5,  r: 258, s: 8  },

  // AI & Data — bottom-right quadrant (0° → 90°)
  { id: 'openai',    label: 'OpenAI',        cat: 'ai',       angle: 6,   r: 296, s: 12 },
  { id: 'llm',       label: 'LLM',           cat: 'ai',       angle: 22,  r: 258, s: 10 },
  { id: 'agents',    label: 'AI Agents',     cat: 'ai',       angle: 37,  r: 298, s: 11 },
  { id: 'embed',     label: 'Embeddings',    cat: 'ai',       angle: 53,  r: 260, s: 9  },
  { id: 'postgres',  label: 'PostgreSQL',    cat: 'ai',       angle: 69,  r: 297, s: 10 },
  { id: 'mongo',     label: 'MongoDB',       cat: 'ai',       angle: 85,  r: 260, s: 9  },

  // Cloud & AWS — bottom-left quadrant (90° → 180°)
  { id: 'lambda',    label: 'Lambda',        cat: 'cloud',    angle: 95,  r: 298, s: 12 },
  { id: 'apigw',     label: 'API Gateway',   cat: 'cloud',    angle: 104, r: 260, s: 10 },
  { id: 's3',        label: 'S3',            cat: 'cloud',    angle: 113, r: 297, s: 11 },
  { id: 'dynamo',    label: 'DynamoDB',      cat: 'cloud',    angle: 122, r: 260, s: 10 },
  { id: 'ec2',       label: 'EC2',           cat: 'cloud',    angle: 131, r: 297, s: 9  },
  { id: 'ecs',       label: 'ECS',           cat: 'cloud',    angle: 140, r: 258, s: 9  },
  { id: 'events',    label: 'EventBridge',   cat: 'cloud',    angle: 149, r: 296, s: 9  },
  { id: 'cw',        label: 'CloudWatch',    cat: 'cloud',    angle: 158, r: 258, s: 9  },
  { id: 'rds',       label: 'RDS',           cat: 'cloud',    angle: 167, r: 296, s: 9  },
  { id: 'aurora',    label: 'Aurora',        cat: 'cloud',    angle: 176, r: 260, s: 8  },
];

// Cross-category edges that tell a real story — what *connects* the quadrants.
const CROSS_LINKS = [
  { from: 'ts',       to: 'node'    },  // TS ↔ Node share the JS runtime
  { from: 'python',   to: 'agents'  },  // Python is the AI language
  { from: 'node',     to: 'lambda'  },  // Node runs on Lambda
  { from: 'fastapi',  to: 'apigw'   },  // FastAPI behind API Gateway
  { from: 'postgres', to: 'rds'     },  // Postgres on RDS
  { from: 'mongo',    to: 'dynamo'  },  // NoSQL parallel
  { from: 'openai',   to: 'embed'   },  // Embeddings come from LLM providers
];

type Hover = { type: 'center' | 'cat' | 'tech'; id: string } | null;

export default function TechGraph() {
  const [hov, setHov] = useState<Hover>(null);

  const getCat = (id: string) => CATS.find(c => c.id === id)!;
  const getTech = (id: string) => TECHS.find(t => t.id === id);

  function catActive(catId: string) {
    if (!hov) return false;
    if (hov.type === 'center') return true;
    if (hov.type === 'cat') return hov.id === catId;
    if (hov.type === 'tech') return getTech(hov.id)?.cat === catId;
    return false;
  }

  function techActive(techId: string) {
    if (!hov) return false;
    if (hov.type === 'center') return true;
    if (hov.type === 'cat') return getTech(techId)?.cat === hov.id;
    if (hov.type === 'tech') return hov.id === techId;
    return false;
  }

  const any = hov !== null;
  const nodeOp = (active: boolean) => any ? (active ? 1 : 0.12) : 1;
  const catEdgeOp = (catId: string) => any ? (catActive(catId) ? 1 : 0.05) : 0.5;
  const techEdgeOp = (techId: string) => any ? (techActive(techId) ? 0.85 : 0.04) : 0.28;
  const crossEdgeOp = (fromId: string, toId: string) => {
    if (!hov) return 0.1;
    return techActive(fromId) || techActive(toId) ? 0.45 : 0.02;
  };

  return (
    <div className="tech-graph-wrap" style={{ position: 'relative' }}>
      <p
        style={{
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          fontFamily: 'var(--mono)',
          fontSize: 11,
          letterSpacing: '0.1em',
          color: 'var(--ink-3)',
          opacity: hov ? 0 : 1,
          transition: 'opacity 0.3s',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}
      >
        hover to explore
      </p>

      <svg
        viewBox="0 0 920 720"
        style={{ width: '100%', height: 'auto', display: 'block' }}
        aria-label="Interactive tech stack graph — hover nodes to explore"
      >
        <defs>
          <radialGradient id="tg-glow">
            <stop offset="0%"   stopColor="#FF6B1A" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#FF6B1A" stopOpacity="0"    />
          </radialGradient>
          <filter id="tg-blur">
            <feGaussianBlur stdDeviation="2.5" />
          </filter>
        </defs>

        <circle className="tg-glow" cx={CX} cy={CY} r="110" fill="url(#tg-glow)" />

        {/* cross-links between related techs across categories */}
        {CROSS_LINKS.map(({ from, to }) => {
          const f = TECHS.find(t => t.id === from)!;
          const t = TECHS.find(t => t.id === to)!;
          const fp = pt(f.r, f.angle);
          const tp = pt(t.r, t.angle);
          return (
            <line key={`xl-${from}-${to}`}
              className="tg-cross tg-dash"
              x1={fp.x} y1={fp.y} x2={tp.x} y2={tp.y}
              stroke="var(--ink-3)"
              strokeWidth="0.6"
              strokeDasharray="2 6"
              strokeOpacity={crossEdgeOp(from, to)}
              style={{ transition: 'stroke-opacity 0.22s' }}
            />
          );
        })}

        {/* center → category edges */}
        {CATS.map(cat => {
          const p = pt(CAT_R, cat.angle);
          const active = catActive(cat.id);
          return (
            <line key={`ce-${cat.id}`}
              className="tg-cat-edge"
              x1={CX} y1={CY} x2={p.x} y2={p.y}
              stroke={cat.color}
              strokeWidth={active ? 1.8 : 0.9}
              strokeOpacity={catEdgeOp(cat.id)}
              style={{ transition: 'stroke-opacity 0.22s, stroke-width 0.22s' }}
            />
          );
        })}

        {/* category → tech edges */}
        {TECHS.map(tech => {
          const cat = getCat(tech.cat);
          const cp = pt(CAT_R, cat.angle);
          const tp = pt(tech.r, tech.angle);
          return (
            <line key={`te-${tech.id}`}
              className="tg-tech-edge tg-dash"
              x1={cp.x} y1={cp.y} x2={tp.x} y2={tp.y}
              stroke={cat.color}
              strokeWidth="0.8"
              strokeDasharray="3 7"
              strokeOpacity={techEdgeOp(tech.id)}
              style={{ transition: 'stroke-opacity 0.22s' }}
            />
          );
        })}

        {/* category nodes */}
        {CATS.map(cat => {
          const p  = pt(CAT_R, cat.angle);
          const lp = pt(CAT_R + 36, cat.angle);
          const active = catActive(cat.id);
          return (
            <g key={cat.id}
              className="tg-cat"
              style={{ opacity: nodeOp(active), transition: 'opacity 0.22s', cursor: 'pointer' }}
              onMouseEnter={() => setHov({ type: 'cat', id: cat.id })}
              onMouseLeave={() => setHov(null)}
            >
              {active && (
                <circle cx={p.x} cy={p.y} r="32" fill={cat.color} opacity="0.12" filter="url(#tg-blur)" />
              )}
              <circle cx={p.x} cy={p.y} r="21" fill="var(--paper)" stroke={cat.color} strokeWidth={active ? 1.8 : 1.2} />
              <circle cx={p.x} cy={p.y} r="8"  fill={cat.color} opacity={active ? 1 : 0.6} />
              <text
                x={lp.x} y={lp.y}
                textAnchor={anchor(lp.x)}
                dominantBaseline="middle"
                fontFamily="monospace"
                fontSize="11.5"
                fontWeight={active ? '600' : '400'}
                fill={cat.color}
                letterSpacing="0.05em"
              >
                {cat.label}
              </text>
            </g>
          );
        })}

        {/* tech nodes — each has its own radius and size */}
        {TECHS.map(tech => {
          const p  = pt(tech.r, tech.angle);
          const lp = pt(tech.r + tech.s + 16, tech.angle);
          const cat = getCat(tech.cat);
          const active = techActive(tech.id);
          return (
            <g key={tech.id}
              className="tg-tech"
              style={{ opacity: nodeOp(active), transition: 'opacity 0.22s', cursor: 'pointer' }}
              onMouseEnter={() => setHov({ type: 'tech', id: tech.id })}
              onMouseLeave={() => setHov(null)}
            >
              <circle cx={p.x} cy={p.y} r={tech.s} fill="var(--paper)" stroke={cat.color} strokeWidth={active ? 1.4 : 0.9} />
              <circle cx={p.x} cy={p.y} r={active ? tech.s * 0.55 : tech.s * 0.38} fill={cat.color} opacity={active ? 1 : 0.55} />
              <text
                x={lp.x} y={lp.y}
                textAnchor={anchor(lp.x)}
                dominantBaseline="middle"
                fontFamily="monospace"
                fontSize="10.5"
                fill={active ? cat.color : 'var(--ink-3)'}
                letterSpacing="0.04em"
                style={{ transition: 'fill 0.22s' }}
              >
                {tech.label}
              </text>
            </g>
          );
        })}

        {/* center node — drawn last */}
        <g
          className="tg-center"
          style={{ cursor: 'pointer' }}
          onMouseEnter={() => setHov({ type: 'center', id: 'center' })}
          onMouseLeave={() => setHov(null)}
        >
          <circle cx={CX} cy={CY} r="54" fill="var(--paper)" stroke="#FF6B1A" strokeWidth="1.6" />
          <circle cx={CX} cy={CY} r="46" fill="var(--paper-2)" stroke="#FF6B1A" strokeWidth="0.5" strokeOpacity="0.45" />
          <text
            x={CX} y={CY - 9}
            textAnchor="middle"
            fontFamily="monospace"
            fontSize="15"
            fontWeight="700"
            fill="var(--ink)"
            letterSpacing="0.06em"
          >
            Nivyan
          </text>
          <text
            x={CX} y={CY + 9}
            textAnchor="middle"
            fontFamily="monospace"
            fontSize="7.5"
            fill="#FF6B1A"
            letterSpacing="0.14em"
          >
            FULL·STACK
          </text>
        </g>
      </svg>
    </div>
  );
}
